(() => {
  const MODEL_VIEWER_RUNTIME = 'https://media.gamedayflags.com/GAMEDAY/SHARED/RUNTIMES/MODEL-VIEWER/4.3.1-297ed2b/MODEL-VIEWER__IOS-THIRDPARTY-QUICKLOOK-FIX__297ed2b.min.js';

  const loadModelViewer = () => {
    if (customElements.get('model-viewer')) return Promise.resolve();
    window.__gamedayModelViewerPromise ||= import(MODEL_VIEWER_RUNTIME);
    return window.__gamedayModelViewerPromise;
  };

  const formatMoney = (cents, currency) => new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0
  }).format(cents / 100);

  class GamedayAuthenticKitConfigurator extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.catalog = [];
      this.filtered = [];
      this.selected = null;
      this.viewer = this.querySelector('[data-kit-model]');
      // New models open at the authored reset view without an idle rotation or hand prompt.
      this.viewer?.addEventListener('load', () => this.resetView());
      this.search = this.querySelector('[data-kit-design-search]');
      this.results = this.querySelector('[data-kit-design-results]');
      this.selection = this.querySelector('[data-kit-design-selection]');
      this.preview = this.querySelector('[data-kit-design-preview]');
      this.mount = this.querySelector('[data-kit-mount]');
      this.quantity = this.querySelector('[data-kit-quantity]');
      this.price = this.querySelector('[data-kit-total]');
      this.addButton = this.querySelector('[data-kit-add]');
      this.status = this.querySelector('[data-kit-status]');
      this.arButton = this.querySelector('[data-kit-ar]');
      this.arGuide = this.querySelector('[data-kit-ar-guide]');
      this.arMessage = this.querySelector('[data-kit-ar-message]');
      this.supportLink = this.querySelector('[data-kit-support]');
      this.pirateLink = this.querySelector('[data-kit-pirate]');
      this.flagContent = this.querySelector('[data-kit-flag-content]');
      this.proofStatus = this.querySelector('[data-kit-proof-status]');
      this.supportLink?.addEventListener('click', (event) => {
        if (!this.selected) return;
        event.preventDefault();
        this.openSigning();
      });
      this.events = new AbortController();
      document.addEventListener('gameday-share-open', (event) => {
        if (event.detail?.productHandle !== this.selected?.handle) return;
        event.preventDefault();
        this.shareSelectedKit();
      }, {signal:this.events.signal});
      this.expandButton = this.querySelector('[data-kit-expand]');
      this.expandButton?.addEventListener('click', () => this.toggleExpanded());
      this.querySelector('[data-kit-design-prev]')?.addEventListener('click', () => this.browseDesign(-1));
      this.querySelector('[data-kit-design-next]')?.addEventListener('click', () => this.browseDesign(1));
      document.addEventListener('fullscreenchange', () => this.syncExpanded(), {signal:this.events.signal});
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.classList.contains('is-expanded') && !document.fullscreenElement) this.exitExpanded();
      }, {signal:this.events.signal});
      this.currency = this.dataset.currency || 'USD';
      this.kitPrice = Number(this.dataset.kitPriceCents || 0);
      this.mountPrice = Number(this.dataset.mountPriceCents || 0);

      this.addEventListener('contextmenu', (event) => event.preventDefault());
      this.addEventListener('dragstart', (event) => event.preventDefault());
      this.search?.addEventListener('focus', () => this.openResults());
      this.search?.addEventListener('input', () => this.filterResults());
      this.search?.addEventListener('keydown', (event) => this.onSearchKeydown(event));
      this.results?.addEventListener('click', (event) => {
        const option = event.target.closest('[data-kit-design-option]');
        if (option) this.selectByHandle(option.dataset.handle, true);
      });
      this.results?.addEventListener('keydown', (event) => this.onResultsKeydown(event));
      this.mount?.addEventListener('change', () => this.updatePrice());
      this.addButton?.addEventListener('click', () => this.addToCart());
      this.arButton?.addEventListener('click', () => this.openAr());
      this.querySelector('[data-kit-ar-continue]')?.addEventListener('click', () => this.startAr());
      this.querySelector('[data-kit-ar-cancel]')?.addEventListener('click', () => this.arGuide?.close());
      this.arGuide?.addEventListener('close', () => this.arButton?.focus({ preventScroll: true }));
      this.querySelector('[data-kit-view-reset]')?.addEventListener('click', () => this.resetView());
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.closeResults();
      });

      this.updatePrice();
      loadModelViewer().catch(() => this.setStatus('The 3D viewer could not load. Your kit options are still available below.', true));
      this.loadCatalog();
    }

    browseDesign(direction) {
      const choices = this.catalog;
      if (!choices.length) return;
      const index = choices.findIndex(item => item.handle === this.selected?.handle);
      this.selectByHandle(choices[(index + direction + choices.length) % choices.length].handle);
    }

    updateOrderState() {
      const released = this.canOrder();
      if (this.addButton) this.addButton.disabled = !released;
      if (this.quantity) this.quantity.disabled = !released;
      const note = this.querySelector('[data-kit-order-note]');
      if (note) note.hidden = released;
      const priceLabel = this.querySelector('[data-kit-price-label]');
      if (priceLabel) priceLabel.textContent = released ? 'Kit price' : 'Pre-release reference';
    }

    async toggleExpanded() {
      if (document.fullscreenElement === this || this.classList.contains('is-expanded')) {
        await this.exitExpanded();
        return;
      }
      // Fullscreen the complete configurator, keeping all controls and AR dialogs with it.
      if (this.requestFullscreen) {
        try { await this.requestFullscreen(); this.syncExpanded(); return; } catch (_) { /* iOS uses the same full-window layout. */ }
      }
      this.savedPageOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.classList.add('is-expanded');
      this.syncExpanded();
    }

    restorePageScroll() {
      if (this.savedPageOverflow !== undefined) {
        document.documentElement.style.overflow = this.savedPageOverflow;
        this.savedPageOverflow = undefined;
      }
      this.classList.remove('is-expanded');
    }

    async exitExpanded() {
      if (document.fullscreenElement === this) await document.exitFullscreen();
      this.restorePageScroll();
      this.syncExpanded();
    }

    syncExpanded() {
      const expanded = document.fullscreenElement === this || this.classList.contains('is-expanded');
      if (this.expandButton) {
        this.expandButton.setAttribute('aria-expanded', String(expanded));
        this.expandButton.textContent = expanded ? 'Close full screen' : 'Full screen';
      }
    }

    async resetView() {
      const viewer = this.viewer;
      if (!viewer) return;
      viewer.cameraOrbit = viewer.dataset.homeCameraOrbit || viewer.getAttribute('camera-orbit') || 'auto auto auto';
      viewer.cameraTarget = viewer.getAttribute('camera-target') || 'auto auto auto';
      viewer.fieldOfView = viewer.getAttribute('field-of-view') || '30deg';
      await viewer.updateComplete;
      viewer.resetTurntableRotation?.(0);
      viewer.jumpCameraToGoal?.();
    }

    async loadCatalog() {
      try {
        const response = await fetch(this.dataset.catalogUrl, { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`Catalog request failed (${response.status})`);
        const payload = await response.json();
        if (payload.status !== 'PASS' || payload.count !== 383 || !Array.isArray(payload.designs)) {
          throw new Error('Catalog validation failed');
        }
        this.catalog = payload.designs;
        this.filtered = this.catalog;
        const query = new URLSearchParams(window.location.search);
        const requested = query.get('design');
        const defaultHandle = requested || this.dataset.defaultHandle;
        this.renderResults();
        this.selectByHandle(defaultHandle, false, true);
        if (query.get('mount') === 'surface' && this.mount && !this.mount.disabled) {
          this.mount.checked = true;
          this.updatePrice();
        }
        this.setStatus('Drag to rotate · Scroll or pinch to zoom');
      } catch (error) {
        this.setStatus('The design catalog did not load. Refresh this page to try again.', true);
        if (this.addButton) this.addButton.disabled = true;
      }
    }

    filterResults() {
      const term = (this.search?.value || '').trim().toLocaleLowerCase();
      this.filtered = this.catalog.filter((item) => {
        const haystack = `${item.name} ${item.id} ${item.handle} ${item.league}`.toLocaleLowerCase();
        return haystack.includes(term);
      });
      this.renderResults();
      this.openResults();
    }

    renderResults() {
      if (!this.results) return;
      this.results.replaceChildren();
      const fragment = document.createDocumentFragment();
      this.filtered.forEach((item) => {
        const option = document.createElement('button');
        option.type = 'button';
        option.className = 'gameday-authentic-kit__option';
        option.dataset.kitDesignOption = '';
        option.dataset.handle = item.handle;
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', String(this.selected?.handle === item.handle));

        const swatches = document.createElement('span');
        swatches.className = 'gameday-authentic-kit__swatches';
        swatches.setAttribute('aria-hidden', 'true');
        const primary = document.createElement('i');
        primary.style.backgroundColor = item.primary;
        const secondary = document.createElement('i');
        secondary.style.backgroundColor = item.secondary;
        swatches.append(primary, secondary);

        const copy = document.createElement('span');
        const name = document.createElement('b');
        name.textContent = item.name.replace(/ Authentic Gameday Flag$/i, '');
        const league = document.createElement('small');
        league.textContent = item.league;
        copy.append(name, league);
        option.append(swatches, copy);
        fragment.append(option);
      });
      if (!this.filtered.length) {
        const empty = document.createElement('p');
        empty.className = 'gameday-authentic-kit__empty';
        empty.textContent = 'No matching designs. Try a team, city, or school name.';
        fragment.append(empty);
      }
      this.results.append(fragment);
    }

    selectByHandle(handle, focusSearch = false, allowFallback = false) {
      const item = this.catalog.find((record) => record.handle === handle)
        || (allowFallback ? this.catalog.find((record) => record.handle === this.dataset.defaultHandle) : null)
        || (allowFallback ? this.catalog[0] : null);
      if (!item) return;
      this.selected = item;
      this.style.setProperty('--kit-primary', item.primary);
      this.style.setProperty('--kit-secondary', item.secondary);
      if (this.viewer && this.viewer.getAttribute('src') !== item.glb) {
        this.viewer.setAttribute('src', item.glb);
        this.viewer.setAttribute('alt', `Interactive ${item.name} with MVP flagpole and Surface Mount`);
      }
      if (this.search) this.search.value = item.name.replace(/ Authentic Gameday Flag$/i, '');
      if (this.selection) {
        this.selection.querySelector('strong').textContent = item.name.replace(/ Authentic Gameday Flag$/i, '');
        this.selection.querySelector('span').textContent = item.league;
      }
      if (this.preview) {
        this.preview.src = item.preview;
        this.preview.alt = `${item.name} design preview`;
      }
      this.filtered = this.catalog;
      this.renderResults();
      if (focusSearch) this.search?.focus({ preventScroll: true });
      this.closeResults();
      this.setStatus(`${item.name} selected.`);
      if (this.addButton) this.addButton.disabled = !this.canOrder();
      this.updateOrderState();
      this.updateActionLinks();
      const query = new URLSearchParams(location.search);
      query.set('design', item.handle);
      history.replaceState(history.state, '', `${location.pathname}?${query}${location.hash}`);
      this.proofReady = this.loadFlagPresentation(item);
    }

    openResults() {
      if (!this.results || !this.search) return;
      this.results.hidden = false;
      this.search.setAttribute('aria-expanded', 'true');
    }

    closeResults() {
      if (!this.results || !this.search) return;
      this.results.hidden = true;
      this.search.setAttribute('aria-expanded', 'false');
    }

    onSearchKeydown(event) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.openResults();
        this.results?.querySelector('[data-kit-design-option]')?.focus();
      } else if (event.key === 'Escape') {
        this.closeResults();
      } else if (event.key === 'Enter' && this.filtered.length === 1) {
        event.preventDefault();
        this.selectByHandle(this.filtered[0].handle, true);
      }
    }

    onResultsKeydown(event) {
      const current = event.target.closest('[data-kit-design-option]');
      if (!current) return;
      const options = [...this.results.querySelectorAll('[data-kit-design-option]')];
      const index = options.indexOf(current);
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        options[(index + 1) % options.length]?.focus();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        options[(index - 1 + options.length) % options.length]?.focus();
      } else if (event.key === 'Escape') {
        this.closeResults();
        this.search?.focus();
      }
    }

    updatePrice() {
      const includeMount = Boolean(this.mount?.checked && !this.mount.disabled);
      const total = this.kitPrice + (includeMount ? this.mountPrice : 0);
      if (this.price) this.price.textContent = formatMoney(total, this.currency);
      const buttonPrice = this.addButton?.querySelector('[data-kit-button-price]');
      if (buttonPrice) buttonPrice.textContent = formatMoney(total, this.currency);
      this.updateOrderState();
      this.updateActionLinks();
    }

    updateActionLinks() {
      if (!this.selected) return;
      const item = this.selected;
      if (this.supportLink) this.supportLink.href = `/products/${item.handle}#sign-the-flag`;
      const flagLink = this.querySelector('[data-kit-flag-link]');
      if (flagLink) flagLink.href = `/products/${item.handle}`;
      if (!this.pirateLink || !/^#[0-9a-f]{6}$/i.test(item.primary) || !/^#[0-9a-f]{6}$/i.test(item.secondary)) return;
      const url = new URL('/products/custom-gameday-mvp-flagpole-kit', location.origin);
      if (/^\d+$/.test(this.dataset.pirateKitVariantId || '')) url.searchParams.set('variant', this.dataset.pirateKitVariantId);
      const start = new URLSearchParams({v:'2', artwork:'jolly-roger', mode:'pirate', size:'3x5',
        name:item.name.replace(/ Authentic Gameday Flag$/i, '') + ' colors / Pirate Flag Kit',
        primary:item.primary, secondary:item.secondary});
      if (this.mount?.checked && !this.mount.disabled) start.set('mount', 'surface');
      url.hash = 'gameday-start=' + start;
      this.pirateLink.href = url.pathname + url.search + url.hash;
    }

    async loadFlagPresentation(item) {
      if (!this.flagContent) return false;
      this.proofRequest?.abort();
      this.proofRequest = new AbortController();
      const request = this.proofRequest;
      window.GamedayFlagSigning?.unmount(this.flagContent);
      this.flagContent.replaceChildren();
      this.proofStatus.textContent = 'Loading ' + item.name.replace(/ Authentic Gameday Flag$/i, '') + '…';
      this.supportLink?.setAttribute('aria-busy', 'true');
      this.querySelector('[data-kit-proof-title]').textContent = item.name.replace(/ Authentic Gameday Flag$/i, '');
      try {
        const response = await fetch(`/products/${encodeURIComponent(item.handle)}`, {credentials:'same-origin', signal:request.signal});
        if (!response.ok) throw new Error('Flag presentation unavailable');
        const html = await response.text();
        if (html.length > 2000000 || request.signal.aborted || this.selected !== item) return false;
        const source = new DOMParser().parseFromString(html, 'text/html');
        const guestbook = source.querySelector('[data-gameday-guestbook]');
        const sourceUrl = new URL(guestbook?.dataset.guestbookSrc || '', location.origin);
        if (guestbook && (guestbook.dataset.guestbookHandle !== item.handle || sourceUrl.origin !== 'https://gamedayflags.com' || sourceUrl.pathname !== '/sign-the-flag/' + item.handle)) throw new Error('Signing proof mismatch');
        const figures = [...source.querySelectorAll('[data-gameday-authentic-personalizer] .gameday-authentic-personalizer__detail-grid figure')].filter(figure => figure.querySelector('img'));
        if (figures.length !== 2) throw new Error('Both flag proofs are required');
        const scope = document.createElement('div');
        scope.dataset.gamedayAuthenticPersonalizer = '';
        scope.className = 'gameday-kit-flag-presentation__content';
        const construction = document.createElement('div');
        construction.className = 'gameday-kit-flag-presentation__construction';
        const imageFigure = (original, label) => {
          const img = original.querySelector('img'), url = new URL(img?.getAttribute('src') || '', location.origin);
          if (url.protocol !== 'https:' || !['exolox.com','www.exolox.com','cdn.shopify.com'].includes(url.hostname)) throw new Error('Unexpected proof image');
          const figure = document.createElement('figure'), image = document.createElement('img'), caption = document.createElement('figcaption');
          image.src = url.href; image.alt = img.alt; image.loading = 'lazy'; image.decoding = 'async';
          image.width = Number(img.getAttribute('width')) || 2000; image.height = Number(img.getAttribute('height')) || 2000;
          caption.textContent = label; figure.append(image, caption); return figure;
        };
        const constructionFigures = [...source.querySelectorAll('.gameday-authentic-personalizer__construction-pair:not(.is-hidden) figure')].slice(0,2);
        if (constructionFigures.length) {
          const heading = document.createElement('header'), eyebrow = document.createElement('span'), title = document.createElement('strong');
          eyebrow.textContent = 'STACKED CONSTRUCTION PREVIEW';
          title.textContent = 'TOP + UNDERSIDE / CONTINUOUS HEADER';
          heading.append(eyebrow,title); construction.append(heading);
          const stack = document.createElement('div');
          stack.className = 'gameday-kit-flag-presentation__construction-stack';
          constructionFigures.forEach((original,index) => {
            const figure = imageFigure(original,index ? 'UNDERSIDE / SEWN HEADER' : 'TOP / WOVEN LOGO TAG');
            const caption = figure.querySelector('figcaption'), number = document.createElement('span');
            number.textContent = String(index + 1).padStart(2,'0');
            caption.prepend(number); stack.append(figure);
          });
          construction.append(stack);
        }
        const details = document.createElement('div');
        details.className = 'gameday-authentic-personalizer__details';
        const grid = document.createElement('div');
        grid.className = 'gameday-authentic-personalizer__detail-grid';
        figures.forEach((figure,index) => grid.append(imageFigure(figure,index ? 'Back / Side B' : 'Front / Side A')));
        if (!guestbook) {
          details.append(grid); scope.append(construction,details); this.flagContent.append(scope);
          this.proofStatus.textContent = 'Autographs are not open for this design yet.';
          return false;
        }
        const book = document.createElement('section');
        book.id = 'sign-the-flag'; book.className = 'gameday-sign'; book.dataset.gamedayGuestbook = '';
        for (const [key,value] of Object.entries(guestbook.dataset)) if (key.startsWith('guestbook')) book.dataset[key] = value;
        book.dataset.guestbookAutoOpen = 'false';
        const intro = document.createElement('div'); intro.className = 'gameday-sign__intro';
        const sign = document.createElement('button'); sign.type = 'button'; sign.dataset.openGuestbook = ''; sign.textContent = 'SIGN HERE TO SUPPORT YOUR TEAM';
        const status = document.createElement('p'); status.dataset.guestbookStatus = ''; status.setAttribute('role','status');
        intro.append(sign,status);
        const holder = document.createElement('div'); holder.dataset.guestbookFrame = ''; holder.hidden = true;
        book.append(intro,holder); details.append(grid,book); scope.append(construction,details);
        this.flagContent.append(scope);
        window.GamedayFlagSigning.mount(this.flagContent);
        this.proofStatus.textContent = '';
        return true;
      } catch (error) {
        if (request.signal.aborted || this.selected !== item) return false;
        this.proofStatus.replaceChildren(document.createTextNode('The selected flag proof could not load. '));
        const fallback = document.createElement('a'); fallback.href = `/products/${item.handle}#sign-the-flag`; fallback.textContent = 'Open this flag to sign it →';
        this.proofStatus.append(fallback);
        return false;
      } finally {
        if (this.selected === item) this.supportLink?.removeAttribute('aria-busy');
      }
    }

    async openSigning() {
      await this.exitExpanded();
      const item = this.selected;
      const ready = await this.proofReady;
      if (this.selected !== item) return;
      const root = this.flagContent?.querySelector('[data-gameday-guestbook]');
      if (ready && root) window.GamedayFlagSigning.open(root);
      else location.assign(`/products/${item.handle}#sign-the-flag`);
    }

    async shareSelectedKit() {
      const status = this.flagContent?.querySelector('[data-share-status]');
      const url = new URL(location.pathname, location.origin);
      url.searchParams.set('design', this.selected.handle);
      if (this.mount?.checked && !this.mount.disabled) url.searchParams.set('mount','surface');
      url.hash = 'gameday-authentic-kit-configurator';
      const share = {title:this.selected.name + ' + MVP Kit',text:'Sign this flag and support your team.',url:url.href};
      try {
        if (navigator.share) await navigator.share(share);
        else {await navigator.clipboard.writeText(share.url);if(status)status.textContent='Kit link copied.';}
      } catch (error) {
        if (error.name !== 'AbortError' && status) status.textContent='Your kit link: ' + share.url;
      }
    }

    disconnectedCallback() {
      if (this.classList.contains('is-expanded')) this.restorePageScroll();
      this.proofRequest?.abort();
      this.events?.abort();
      if (this.flagContent) window.GamedayFlagSigning?.unmount(this.flagContent);
    }

    canOrder() {
      return this.dataset.orderingMode === 'retail' && this.dataset.kitAvailable === 'true' && this.selected?.retailReleased === true;
    }

    async addToCart() {
      if (!this.canOrder()) {
        if (this.addButton) this.addButton.disabled = true;
        this.setStatus('Partner proof only. Retail ordering is closed. Contact Gameday Flags for an authorized sample inquiry.', true);
        return;
      }
      if (!this.selected || !this.addButton) {
        this.setStatus('Choose an Authentic flag design first.', true);
        this.search?.focus();
        return;
      }
      const quantity = Math.max(1, Math.min(10, Number.parseInt(this.quantity?.value || '1', 10) || 1));
      const includeMount = Boolean(this.mount?.checked && !this.mount.disabled);
      const common = {
        'Authentic flag design': this.selected.name,
        'Design ID': this.selected.id,
        'Design product': this.selected.handle,
        'Interactive asset revision': this.selected.revision
      };
      const items = [{
        id: Number(this.dataset.kitVariantId),
        quantity,
        properties: {
          ...common,
          'Included carry bag': 'Stock black Gameday carry bag / free with kit',
          'Surface Mount': includeMount ? 'Included as a separate kit component' : 'Not included'
        }
      }];
      if (includeMount) {
        items.push({
          id: Number(this.dataset.mountVariantId),
          quantity,
          properties: {
            ...common,
            'Bundle role': 'MVP Surface Mount for complete Authentic kit'
          }
        });
      }

      this.addButton.disabled = true;
      this.addButton.setAttribute('aria-busy', 'true');
      this.setStatus('Adding your complete setup…');
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || 'The kit could not be added.');
        }
        window.location.assign(`${root}cart`);
      } catch (error) {
        this.setStatus(error.message || 'The kit could not be added. Please try again.', true);
        this.addButton.disabled = !this.canOrder();
        this.addButton.removeAttribute('aria-busy');
      }
    }

    openAr() {
      if (!this.viewer || !this.arGuide) return;
      if (this.arMessage) this.arMessage.textContent = '';
      if (!this.arGuide.open) this.arGuide.showModal();
    }

    async startAr() {
      if (this.arLaunching || !this.arGuide?.open) return;
      if (typeof this.viewer?.activateAR !== 'function' || this.viewer.loaded === false) {
        if (this.arMessage) this.arMessage.textContent = 'The 3D viewer is still loading. Tap OPEN AR again in a moment.';
        return;
      }
      if (!this.viewer.canActivateAR) {
        if (this.arMessage) this.arMessage.textContent = 'AR is not available in this browser. Open this product page on an AR-capable phone to place your flag.';
        return;
      }
      this.arLaunching = true;
      try {
        // Launch directly from the Continue click to preserve the mobile user gesture.
        const launch = this.viewer.activateAR();
        this.arGuide.close();
        await launch;
      } catch (error) {
        if (!this.arGuide.open) this.arGuide.showModal();
        if (this.arMessage) this.arMessage.textContent = 'AR did not open. Tap OPEN AR to try again, or go back to your flag.';
        this.setStatus('AR did not open. Keep this page open and tap VIEW IN YOUR SPACE again.', true);
      } finally {
        this.arLaunching = false;
      }
    }

    setStatus(message, isError = false) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.dataset.error = String(isError);
    }
  }

  if (!customElements.get('gameday-authentic-kit-configurator')) {
    customElements.define('gameday-authentic-kit-configurator', GamedayAuthenticKitConfigurator);
  }
})();
